import React from "react";

function normalizeMarkdownishText(value) {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+(\d{1,2})[.)][ \t]+(?=\S)/g, "\n$1. ")
    .replace(/[ \t]+([•*-])[ \t]+(?=\S)/g, "\n$1 ");
}

function renderInline(value) {
  const text = String(value || "").replace(/\*\*(\s*)/g, "**");
  const parts = [];
  const pattern = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index).replace(/\*\*/g, ""));
    }
    parts.push(
      <strong key={`strong-${match.index}`} className="font-semibold text-inherit">
        {match[1].trim()}
      </strong>
    );
    lastIndex = pattern.lastIndex;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex).replace(/\*\*/g, ""));
  }

  return parts.length > 0 ? parts : text.replace(/\*\*/g, "");
}

function buildBlocks(value) {
  const lines = normalizeMarkdownishText(value).split("\n");
  const blocks = [];
  let paragraph = [];
  let ordered = [];
  let unordered = [];

  const flushParagraph = () => {
    const text = paragraph.join(" ").replace(/\s+/g, " ").trim();
    if (text) blocks.push({ type: "paragraph", text });
    paragraph = [];
  };
  const flushOrdered = () => {
    if (ordered.length) blocks.push({ type: "ordered", items: ordered });
    ordered = [];
  };
  const flushUnordered = () => {
    if (unordered.length) blocks.push({ type: "unordered", items: unordered });
    unordered = [];
  };
  const flushLists = () => {
    flushOrdered();
    flushUnordered();
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushLists();
      continue;
    }

    const orderedMatch = line.match(/^(\d{1,2})[.)]\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      flushUnordered();
      ordered.push(orderedMatch[2].trim());
      continue;
    }

    const unorderedMatch = line.match(/^[•*-]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      flushOrdered();
      unordered.push(unorderedMatch[1].trim());
      continue;
    }

    flushLists();
    paragraph.push(line);
  }

  flushParagraph();
  flushLists();
  return blocks;
}

export default function FolaFormattedText({ text, className = "" }) {
  const blocks = React.useMemo(() => buildBlocks(text), [text]);

  if (blocks.length === 0) return null;

  return (
    <div className={["space-y-2 break-words", className].filter(Boolean).join(" ")}>
      {blocks.map((block, index) => {
        if (block.type === "ordered") {
          return (
            <ol
              key={`ordered-${index}`}
              className="ml-5 list-decimal space-y-1 marker:text-[rgb(var(--afrimed-primary))] marker:font-semibold"
            >
              {block.items.map((item, itemIndex) => (
                <li key={`${index}-${itemIndex}`} className="pl-1">
                  {renderInline(item)}
                </li>
              ))}
            </ol>
          );
        }

        if (block.type === "unordered") {
          return (
            <ul
              key={`unordered-${index}`}
              className="ml-5 list-disc space-y-1 marker:text-[rgb(var(--afrimed-primary))]"
            >
              {block.items.map((item, itemIndex) => (
                <li key={`${index}-${itemIndex}`} className="pl-1">
                  {renderInline(item)}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`paragraph-${index}`} className="leading-5">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}
